import { formatDate, type Locale } from '@castfeeds/i18n';

const pad = (n: number) => String(Math.floor(n)).padStart(2, '0');

/** ms → "hh:mm:ss" (timecodes are always rendered LTR). */
export function timecode(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${pad(s / 3600)}:${pad((s % 3600) / 60)}:${pad(s % 60)}`;
}

/** ms → "m:ss" for short clips, "h:mm:ss" past an hour. */
export function clipDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s >= 3600) return `${Math.floor(s / 3600)}:${pad((s % 3600) / 60)}:${pad(s % 60)}`;
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

/** Seconds → "m:ss" for sliders. */
export function mmss(seconds: number): string {
  return `${pad(seconds / 60)}:${pad(seconds % 60)}`;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v >= 100 || u === 0 ? Math.round(v) : v.toFixed(1)} ${units[u]}`;
}

/** Seconds → "1 h 4 min" / "3 min" / "12 s" (LTR, language-neutral units). */
export function eta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.round(seconds);
  if (s >= 3600) return `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`;
  if (s >= 60) return `${Math.round(s / 60)} min`;
  return `${s} s`;
}

export function shortDate(locale: Locale, iso: string | null | undefined): string {
  if (!iso) return '—';
  return formatDate(locale, iso, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function dateTime(locale: Locale, iso: string | null | undefined): string {
  if (!iso) return '—';
  return formatDate(locale, iso, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
}

/** "Today 18:00", "Tomorrow 09:00", else "Wed 23 Oct 10:00" (prototype dayName). */
export function relativeDay(locale: Locale, iso: string, labels: { today: string; tomorrow: string }, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = formatDate(locale, d, { hour: '2-digit', minute: '2-digit', hour12: false });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(d) - day(now)) / 86400_000);
  if (diff === 0) return `${labels.today} ${time}`;
  if (diff === 1) return `${labels.tomorrow} ${time}`;
  return `${formatDate(locale, d, { weekday: 'short', day: 'numeric', month: 'short' })} ${time}`;
}

/** "Now", "8 min", "2 h", "3 d" via Intl.RelativeTimeFormat. */
export function ago(locale: Locale, iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const diff = (new Date(iso).getTime() - now) / 1000;
  if (!Number.isFinite(diff)) return null;
  const rtf = new Intl.RelativeTimeFormat(locale === 'he' ? 'he' : 'en', { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(0, 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  return rtf.format(Math.round(diff / 86400), 'day');
}

export function greetingKey(now = new Date()): 'goodMorning' | 'goodAfternoon' | 'goodEvening' {
  const h = now.getHours();
  return h < 12 ? 'goodMorning' : h < 18 ? 'goodAfternoon' : 'goodEvening';
}

export function splitList(text: string): string[] {
  return text.split(/[,،\n]/).map((s) => s.trim()).filter(Boolean);
}
